import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { PayoutContract, Token, TraderAddress } from '@archax/shared-types'
import { Typography, Table, TableHead, TableBody } from '@mui/material'
import { Wrapper, StyledTableCell, StyledTableRow } from './styles'
import { formatBigNumber, formatUTCMMDDYYYY } from '../../../../util/formatters'

type FormatterInput = {
  value: any
  operationData?: any
  token?: Token
  underlyingToken?: Token
  traderAddress?: TraderAddress
}
type Props = {
  operationData: Record<string, unknown>
  token: Token
  /**
   * The registered/underlying token referenced by the operation data (e.g. the token actually
   * moved by a TRANSFER_TOKEN operation). Its `decimals` can differ from the pool token in
   * `token`, so it is used to format the `amount` field.
   */
  underlyingToken?: Token
  traderAddress?: TraderAddress
  payoutContract?: PayoutContract
}
export default function OperationDetailsModalContent({
  operationData,
  token,
  underlyingToken,
  traderAddress,
  payoutContract,
}: Props): ReactElement {
  const { t, i18n } = useTranslation()
  const formatterMap = useMemo(() => {
    return {
      token: ({ value }: FormatterInput) => value.tokenId || '',
      trader: ({ value }: FormatterInput) => value.recipientAddress || '',
      protocol: ({ value }: FormatterInput) => t(`general.protocols.${value}` as any),
      standard: ({ value }: FormatterInput) => t(`general.standards.${value}` as any),
      tokenType: ({ value }: FormatterInput) => t(`general.tokenTypes.${value}` as any),
      numberToCreate: ({ value, operationData }: FormatterInput) => {
        const decimals = Number(operationData.decimals || 0)
        return new Intl.NumberFormat('en-US', { useGrouping: true, minimumFractionDigits: decimals }).format(
          Number(value) / Math.pow(10, decimals),
        )
      },
      payoutPerIndex: ({ value }: FormatterInput) => {
        const decimals = Number(payoutContract!.payoutToken.decimals || 0)
        return new Intl.NumberFormat('en-US', { useGrouping: true, minimumFractionDigits: decimals }).format(
          Number(value) / Math.pow(10, decimals),
        )
      },
      startAt: ({ value }: FormatterInput) => {
        return formatUTCMMDDYYYY(value)
      },
      endAt: ({ value }: FormatterInput) => {
        return formatUTCMMDDYYYY(value)
      },
      payoutToken: ({ value }: FormatterInput) => {
        return `${value.name} (${value.address})`
      },
      amount: ({ value, token, underlyingToken }: FormatterInput) => {
        // `amount` is scaled with the decimals of the token actually being transferred (the
        // registered/underlying token). That can differ from `token` (the pool token), so prefer
        // the underlying token's decimals and fall back to the pool token's when it is absent.
        const decimals = Number(underlyingToken?.decimals ?? token?.decimals ?? 0)
        return formatBigNumber(value, decimals)
      },
      traderAddressId: ({ traderAddress }: FormatterInput) => {
        return `${traderAddress?.address}`
      },
    } as Record<string, (input: FormatterInput) => string>
  }, [])
  const formatCellValue = useCallback((key: string, value: any) => {
    const formatter = formatterMap[key]
    if (formatter) {
      return formatter({
        value,
        operationData,
        token,
        underlyingToken,
        traderAddress,
      })
    }

    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2)
    }
    if (typeof value === 'string') {
      return value
    }
    if (typeof value === 'number') {
      return value.toString()
    }
    if (typeof value === 'boolean') {
      return value ? t('general.yes') : t('general.no')
    }
    return 'N/A'
  }, [])

  const operationDataList = Object.keys(operationData)
    .map((key) => {
      if (![undefined, null, ''].includes(operationData[key] as any)) {
        return (
          <StyledTableRow key={key}>
            <StyledTableCell>
              {i18n.exists(`general.properties.${key}` as any) ? t(`general.properties.${key}` as any) : key}
            </StyledTableCell>
            <StyledTableCell flex={2} align="right">
              {formatCellValue(key, operationData[key])}
            </StyledTableCell>
          </StyledTableRow>
        )
      }
    })
    .filter((it) => !!it)

  const content =
    operationDataList.length > 0 ? (
      <>{operationDataList}</>
    ) : (
      <Typography variant="body2" color="textSecondary">
        {t('adminApproval.operationDetailsModal.noData')}
      </Typography>
    )

  return (
    <Wrapper>
      <Typography variant="h5" mb={4}>
        {t('adminApproval.operationDetailsModal.title')}
      </Typography>
      <Table>
        <TableHead>
          <StyledTableRow sx={{ display: 'flex', width: '100%' }}>
            <StyledTableCell flex={1}> {t('adminApproval.operationDetailsModal.keyLabel')}</StyledTableCell>
            <StyledTableCell flex={2} align="right">
              {' '}
              {t('adminApproval.operationDetailsModal.valueLabel')}
            </StyledTableCell>
          </StyledTableRow>
        </TableHead>
        <TableBody sx={{ maxHeight: 500, overflowY: 'scroll' }}>{content}</TableBody>
      </Table>
    </Wrapper>
  )
}
